import { useCallback, useEffect, useRef } from 'react';

interface UsePressAndHoldOptions {
  /** Hold duration before the press fires. */
  delayMs?: number;
  /** Movement (px) that cancels the press as a scroll/drag. */
  moveTolerance?: number;
}

interface PressAndHoldHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerLeave: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
}

/**
 * Press-and-hold gesture for both mouse and touch (unified via Pointer Events).
 * Fires `onPressAndHold(x, y)` the moment the primary pointer has been held for
 * `delayMs` without moving more than `moveTolerance` px — i.e. WHILE the pointer
 * is still down (Telegram-style peek), not on release. Firing mid-hold is what
 * makes the preview feel responsive: it appears under your finger as soon as
 * you've held long enough, instead of waiting for you to let go.
 *
 * Because it opens something (a preview overlay) while the pointer is still
 * down, the `click` the eventual release produces is swallowed via a one-shot
 * capture-phase listener on `document`. That stops the click wherever it lands:
 * on the origin element (whose `onClick` selects the channel and marks it read —
 * the preview's whole promise is to leave it unread) AND on the just-mounted
 * overlay backdrop (whose `onClick` would instantly dismiss the preview). It is
 * a single-shot listener with a safety timeout, so it never eats a later,
 * deliberate click.
 *
 * The press is cancelled — before it fires — by movement beyond tolerance (a
 * scroll/drag), leaving the element, a non-primary button, or a cancelled
 * pointer. Once it has fired, later movement is ignored (the hold is done).
 */
export function usePressAndHold(
  onPressAndHold: (x: number, y: number) => void,
  { delayMs = 400, moveTolerance = 12 }: UsePressAndHoldOptions = {},
): PressAndHoldHandlers {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const swallowCleanupRef = useRef<(() => void) | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    startRef.current = null;
  }, [clearTimer]);

  // Eat exactly the next click (the one the release produces), wherever it
  // lands, then self-remove. A safety timeout removes it on the rare cancel
  // paths where no click ever arrives, so a legitimate later click is never
  // swallowed.
  const armClickSwallow = useCallback(() => {
    swallowCleanupRef.current?.();
    const listener = (ev: MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      teardown();
    };
    document.addEventListener('click', listener, true);
    const safetyTimer = setTimeout(() => teardown(), 700);
    function teardown() {
      document.removeEventListener('click', listener, true);
      clearTimeout(safetyTimer);
      swallowCleanupRef.current = null;
    }
    swallowCleanupRef.current = teardown;
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Primary button only (0 for mouse / touch / pen). Right- and
      // middle-click must not shadow the context menu or start a hold.
      if (e.button !== 0) {
        reset();
        return;
      }
      const x = e.clientX;
      const y = e.clientY;
      startRef.current = { x, y };
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        startRef.current = null; // fired — subsequent moves are ignored
        armClickSwallow();
        onPressAndHold(x, y); // open the preview WHILE the pointer is held
      }, delayMs);
    },
    [reset, clearTimer, delayMs, onPressAndHold, armClickSwallow],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const s = startRef.current;
      if (!s) return; // already fired (startRef nulled) or cancelled
      if (Math.abs(e.clientX - s.x) > moveTolerance || Math.abs(e.clientY - s.y) > moveTolerance) {
        reset(); // moved too far before firing → a scroll/drag, not a hold
      }
    },
    [reset, moveTolerance],
  );

  // Released / left / cancelled. If it already fired, the timer is gone and
  // startRef is null, so this is just cleanup. If it hasn't fired yet, this
  // drops the pending timer — a short tap, not a hold.
  const onPointerUp = useCallback(() => reset(), [reset]);

  // Remove a still-armed swallow listener if we unmount mid-gesture.
  useEffect(() => () => swallowCleanupRef.current?.(), []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave: reset,
    onPointerCancel: reset,
  };
}
